"use client"

import { useAuthClient, useUser } from "@sophy-ai/auth/nextjs/client"
import { Button } from "@sophy-ai/ui/button"
import { ChevronDown, LogOut, Menu, X } from "lucide-react"
import Image from "next/image"
import Link from "next/link"
import { useEffect, useState } from "react"
import { FlagIcon } from "./flag-icon"
import { usePageTransition } from "./page-transition"

type Language = "ja" | "en" | "zh"

const LANG_STORAGE_KEY = "sophy-language"

function t(lang: Language, ja: string, en: string, zh?: string): string {
  if (lang === "zh") return zh ?? ja
  if (lang === "en") return en
  return ja
}

export function MarketingHeader() {
  const { isLoaded, isSignedIn } = useUser()
  const { signOut } = useAuthClient()
  const { navigateWithFade } = usePageTransition()
  const [language, setLanguage] = useState<Language>("ja")
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)

  useEffect(() => {
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem(LANG_STORAGE_KEY) as Language | null
      if (saved) setLanguage(saved)
    }
  }, [])

  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.scrollY > 10
      setIsScrolled((prev) => (prev === scrolled ? prev : scrolled))
    }
    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  const changeLanguage = (lang: Language) => {
    setLanguage(lang)
    setIsLangMenuOpen(false)
    if (typeof window !== "undefined") {
      localStorage.setItem(LANG_STORAGE_KEY, lang)
    }
  }

  const handleLogout = async () => {
    await signOut()
    window.location.href = "/"
  }

  const flagSize = "w-full h-full"

  const langOptions: { code: Language; label: string }[] = [
    { code: "ja", label: "日本語" },
    { code: "en", label: "English" },
    { code: "zh", label: "中文" },
  ]

  return (
    <>
      <link href="https://fonts.googleapis.com/css2?family=Sora:wght@400&display=swap" rel="stylesheet" />
      <header
        className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-300 ${
          isScrolled ? "bg-white/80 backdrop-blur-md" : "bg-white"
        } border-b border-gray-100`}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between h-[54px]">
            {/* Logo */}
            <Link
              href="/"
              onClick={(e) => {
                e.preventDefault()
                navigateWithFade("/")
              }}
              className="flex items-center gap-px"
            >
              <Image
                src="/images/sophy_symbol.webp"
                alt="Sophy"
                width={20}
                height={26}
                className="w-[17px] h-[22px] md:w-[16px] md:h-[22px]"
              />
              <span
                className="text-[22px] leading-[22px] md:text-[22px]"
                style={{
                  color: "#000",
                  fontFamily: "Sora, sans-serif",
                  fontWeight: 400,
                }}
              >
                ophy
              </span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              <Link
                href="/use-cases"
                onClick={(e) => {
                  e.preventDefault()
                  navigateWithFade("/use-cases")
                }}
                className="text-sm font-medium transition"
                style={{ color: "#26251f" }}
              >
                {t(language, "活用例", "Use Cases", "使用案例")}
              </Link>

              {/* Language Selector */}
              <div className="relative">
                <button
                  onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
                  className="flex items-center gap-1.5 text-sm font-medium transition"
                  style={{ color: "#26251f" }}
                  aria-label="言語選択"
                >
                  <div className="w-5 h-5 rounded-full overflow-hidden border border-gray-200">
                    <FlagIcon code={language} className={flagSize} />
                  </div>
                  <ChevronDown className="h-4 w-4" />
                </button>
                {isLangMenuOpen && (
                  <div className="absolute top-full right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg py-2 min-w-[140px] z-[98]">
                    {langOptions.map(({ code, label }) => (
                      <button
                        key={code}
                        onClick={() => changeLanguage(code)}
                        className="flex items-center gap-2 w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        <div className="w-4 h-4 rounded-full overflow-hidden border border-gray-200 flex-shrink-0">
                          <FlagIcon code={code} className={flagSize} />
                        </div>
                        {label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Auth Button */}
              {isLoaded && isSignedIn ? (
                <Button variant="outline" size="sm" className="flex items-center gap-2 h-10" onClick={handleLogout}>
                  <LogOut className="h-4 w-4" />
                  {t(language, "サインアウト", "Sign Out", "退出登录")}
                </Button>
              ) : (
                <a href="/api/auth/sign-in">
                  <Button size="sm" className="bg-black hover:bg-gray-800 text-white h-10">
                    {t(language, "ログイン", "Log in", "登录")}
                  </Button>
                </a>
              )}
            </nav>

            {/* Mobile: Language Selector + Menu Button */}
            <div className="md:hidden flex items-center gap-3">
              <div className="relative">
                <button
                  onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
                  className="flex items-center gap-1 px-2 py-2"
                  aria-label="言語選択"
                >
                  <div className="w-6 h-6 rounded-full overflow-hidden border border-gray-200">
                    <FlagIcon code={language} className={flagSize} />
                  </div>
                  <ChevronDown className="h-4 w-4" style={{ color: "#26251f" }} />
                </button>
                {isLangMenuOpen && (
                  <>
                    <div className="fixed inset-0 z-[110]" onClick={() => setIsLangMenuOpen(false)} />
                    <div className="absolute top-full right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg py-2 min-w-[120px] z-[120]">
                      {langOptions.map(({ code, label }) => (
                        <button
                          key={code}
                          onClick={() => changeLanguage(code)}
                          className="flex items-center gap-2 w-full text-left px-4 py-2 text-sm transition"
                          style={{ color: "#26251f" }}
                        >
                          <div className="w-5 h-5 rounded-full overflow-hidden border border-gray-200 flex-shrink-0">
                            <FlagIcon code={code} className={flagSize} />
                          </div>
                          {label}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>

              <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="p-2" aria-label="メニュー">
                {isMobileMenuOpen ? (
                  <X className="h-6 w-6" style={{ color: "#26251f" }} />
                ) : (
                  <Menu className="h-6 w-6" style={{ color: "#26251f" }} />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu Overlay */}
        {isMobileMenuOpen && (
          <div className="fixed inset-0 z-[99] md:hidden animate-fade-in">
            <div
              className="absolute inset-0 bg-black/50 backdrop-blur-sm animate-fade-in"
              onClick={() => setIsMobileMenuOpen(false)}
            />
            <div className="absolute top-[54px] right-0 left-0 bottom-0 bg-white overflow-y-auto animate-slide-down">
              <nav className="px-4 py-6 space-y-4">
                <Link
                  href="/use-cases"
                  className="block text-base font-medium transition"
                  style={{ color: "#26251f" }}
                  onClick={(e) => {
                    e.preventDefault()
                    setIsMobileMenuOpen(false)
                    navigateWithFade("/use-cases")
                  }}
                >
                  {t(language, "活用例", "Use Cases", "使用案例")}
                </Link>

                <div className="pt-4 border-t border-gray-200">
                  {isLoaded && isSignedIn ? (
                    <Button
                      variant="outline"
                      size="default"
                      className="w-full flex items-center justify-center gap-2 h-12"
                      onClick={() => {
                        handleLogout()
                        setIsMobileMenuOpen(false)
                      }}
                    >
                      <LogOut className="h-4 w-4" />
                      {t(language, "サインアウト", "Sign Out", "退出登録")}
                    </Button>
                  ) : (
                    <a href="/api/auth/sign-up" onClick={() => setIsMobileMenuOpen(false)}>
                      <Button size="default" className="w-full bg-black hover:bg-gray-800 text-white h-12">
                        {t(language, "始める", "Get Started", "开始使用")}
                      </Button>
                    </a>
                  )}
                </div>
              </nav>
            </div>
          </div>
        )}
      </header>
    </>
  )
}
